import { useMemo, useState } from 'react';
import { formatarMoeda } from '../../lib/format';
import { Modal } from '../../components/ui';
import { Minus, Plus, ShoppingBag, Check } from 'lucide-react';
import { useCarrinho } from './carrinho';

interface Props {
  produto: any;
  cores: any[];
  tamanhos: any[];
  estampas: any[];
  aberto: boolean;
  onFechar: () => void;
}

export function ConfiguradorModal({ produto, cores, tamanhos, estampas, aberto, onFechar }: Props) {
  const { adicionar } = useCarrinho();
  const [estampaId, setEstampaId] = useState<string | null>(null);
  const [cor, setCor] = useState<string>('');
  const [tamanho, setTamanho] = useState<string>('');
  const [quantidade, setQuantidade] = useState(1);
  const [erro, setErro] = useState('');

  const precoBase = Number(produto?.precoPromocional ?? produto?.precoBase ?? 0);
  const estampaSel = estampas.find(e => e.id === estampaId) || null;
  const tamanhoSel = tamanhos.find(t => t.nome === tamanho) || null;

  const valorUnitario = useMemo(
    () => precoBase + Number(estampaSel?.precoAdicional || 0) + Number(tamanhoSel?.adicional || 0),
    [precoBase, estampaSel, tamanhoSel]
  );

  if (!produto) return null;

  const handleAdicionar = () => {
    if (!cor) { setErro('Escolha uma cor.'); return; }
    if (!tamanho) { setErro('Escolha um tamanho.'); return; }
    setErro('');

    const corObj = cores.find(c => c.nome === cor);
    adicionar({
      chave: `${produto.id}|${tamanho}|${cor}|${estampaId || 'lisa'}`,
      produtoId: produto.id,
      produtoNome: produto.nome,
      produtoImagem: produto.imagem,
      tamanho,
      cor,
      corHex: corObj?.hex,
      estampaId,
      estampaNome: estampaSel?.nome || null,
      estampaImagem: estampaSel?.imagem || null,
      quantidade,
      valorUnitario,
    });
    onFechar();
  };

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo={produto.nome}
      descricao={produto.descricao || 'Monte a peça do seu jeito.'}
      largura="max-w-2xl"
    >
      <div className="space-y-6">
        {/* Estampa */}
        <div>
          <p className="input-label">Estampa</p>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2.5">
            <button
              type="button"
              onClick={() => setEstampaId(null)}
              className={`relative aspect-square rounded-xl border-2 flex flex-col items-center justify-center gap-1 text-2xs font-semibold transition-all ${
                estampaId === null
                  ? 'border-brand-600 bg-brand-50 text-brand-700'
                  : 'border-ink-200 text-ink-400 hover:border-ink-300'
              }`}
            >
              {estampaId === null && <SeloCheck />}
              Lisa
            </button>
            {estampas.map(e => (
              <button
                key={e.id}
                type="button"
                onClick={() => setEstampaId(e.id)}
                title={e.nome}
                className={`relative aspect-square rounded-xl border-2 overflow-hidden transition-all ${
                  estampaId === e.id ? 'border-brand-600 shadow-md' : 'border-ink-200 hover:border-ink-300'
                }`}
              >
                {estampaId === e.id && <SeloCheck />}
                {e.imagem ? (
                  <img src={e.imagem} alt={e.nome} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center text-brand-700 text-2xs font-bold p-1 text-center leading-tight">
                    {e.nome}
                  </div>
                )}
                {Number(e.precoAdicional) > 0 && (
                  <span className="absolute bottom-0 inset-x-0 bg-ink-950/70 text-white text-[10px] font-semibold text-center py-0.5">
                    +{formatarMoeda(e.precoAdicional)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Cor */}
        <div>
          <p className="input-label">Cor {cor && <span className="font-normal text-ink-400">— {cor}</span>}</p>
          <div className="flex flex-wrap gap-2.5">
            {cores.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCor(c.nome)}
                title={c.nome}
                className={`w-9 h-9 rounded-full ring-2 ring-offset-2 transition-all ${
                  cor === c.nome ? 'ring-brand-600 scale-110' : 'ring-ink-200 hover:ring-ink-300'
                }`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
        </div>

        {/* Tamanho */}
        <div>
          <p className="input-label">Tamanho</p>
          <div className="flex flex-wrap gap-2">
            {tamanhos.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTamanho(t.nome)}
                className={`min-w-[52px] px-3 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${
                  tamanho === t.nome
                    ? 'border-brand-600 bg-brand-50 text-brand-700'
                    : 'border-ink-200 text-ink-600 hover:border-ink-300'
                }`}
              >
                {t.nome}
                {Number(t.adicional) > 0 && (
                  <span className="block text-[10px] font-semibold text-ink-400">
                    +{formatarMoeda(t.adicional)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Quantidade + total */}
        <div className="flex items-center justify-between gap-4 rounded-xl bg-ink-50 p-4">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setQuantidade(q => Math.max(1, q - 1))}
              className="icon-btn !bg-white !border !border-ink-200"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-12 text-center font-display text-lg font-bold">{quantidade}</span>
            <button
              type="button"
              onClick={() => setQuantidade(q => q + 1)}
              className="icon-btn !bg-white !border !border-ink-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="text-right">
            <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">
              {formatarMoeda(valorUnitario)} / un.
            </p>
            <p className="font-display text-xl font-bold text-ink-950">
              {formatarMoeda(valorUnitario * quantidade)}
            </p>
          </div>
        </div>

        {erro && (
          <p className="text-[13px] font-medium text-red-600 animate-fade-in">{erro}</p>
        )}

        <button type="button" onClick={handleAdicionar} className="btn-primary w-full !py-3.5 text-[15px]">
          <ShoppingBag className="w-5 h-5" />
          Adicionar ao carrinho
        </button>
      </div>
    </Modal>
  );
}

function SeloCheck() {
  return (
    <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-600 flex items-center justify-center z-10">
      <Check className="w-3 h-3 text-white" />
    </span>
  );
}
